<script setup lang="ts">
import RcCustomerSelect from '@/components/rc/common/RcCustomerSelect.vue'
import type { ISiteIdentifier } from '@/components/rc/common/RcSiteSelect.vue'
import RcSiteSelect from '@/components/rc/common/RcSiteSelect.vue'
import { useTFACurtailmentIntervals } from '@/composables/cpm/cpmProgramManagement'
import type { ICustomer } from '@/types/core/Customer'
import type { ITFACurtailmentIntervalCreationPayload } from '@/types/cpm'
import { Icon } from '@iconify/vue'
import { useNow } from '@vueuse/core'
import type { FormInstance } from 'element-plus'
import { ElMessage } from 'element-plus'
import { DateTime } from 'luxon'
import { reactive, ref, watch, computed } from 'vue'

const visible = defineModel<boolean>()

const props = defineProps<{
  selectedCustomer?: ICustomer
  selectedSite?: ISiteIdentifier
}>()

const { createTFACurtailmentIntervalMutation } = useTFACurtailmentIntervals()

// Selected customer for RcCustomerSelect (initialized with prop if provided)
const selectedCustomer = ref<ICustomer | undefined>(props.selectedCustomer)

// Selected site for RcSiteSelect (initialized with prop if provided)
const selectedSite = ref<ISiteIdentifier | undefined>(props.selectedSite)

const formRef = ref<FormInstance>()
const isSubmitting = ref(false)
const form = reactive({
  customer_id: undefined as number | undefined,
  site_id: props.selectedSite?.site_id as string | undefined,
  timeRange: undefined as [string, string] | undefined,
  curtailed: false,
})

watch(
  selectedCustomer,
  (newCustomer) => {
    form.customer_id = newCustomer?.id
  },
  { immediate: true }
)

watch(
  selectedSite,
  (newSite) => {
    form.site_id = newSite?.site_id
  },
  { immediate: true }
)

const siteHasTimezone = computed(() => {
  return selectedSite.value && !!selectedSite.value.timezone
})

const selectedHoursCount = computed(() => {
  if (!form.timeRange) return null
  const start = DateTime.fromISO(form.timeRange[0])
  const end = DateTime.fromISO(form.timeRange[1])
  return end.diff(start, 'hours').hours
})

const rules = {
  customer_id: [{ required: true, message: 'Please select a customer', trigger: 'change' }],
  site_id: [{ required: true, message: 'Please select a site', trigger: 'change' }],
  timeRange: [{ required: true, message: 'Please select a time range', trigger: 'change' }],
}

const submitForm = async () => {
  if (!formRef.value) return

  try {
    await formRef.value.validate()

    if (!siteHasTimezone.value) {
      ElMessage.error('Site must have a valid timezone')
      return
    }

    isSubmitting.value = true

    const siteTimezone = selectedSite.value!.timezone!
    const payload: ITFACurtailmentIntervalCreationPayload = {
      site_id: form.site_id!,
      start_datetime: DateTime.fromISO(form.timeRange![0], { zone: siteTimezone }).toUTC().toISO()!,
      end_datetime: DateTime.fromISO(form.timeRange![1], { zone: siteTimezone }).toUTC().toISO()!,
      curtailed: form.curtailed,
    }

    await createTFACurtailmentIntervalMutation.mutateAsync(payload)

    visible.value = false
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Form submission error:', error)
  } finally {
    isSubmitting.value = false
  }
}

const resetForm = () => {
  form.customer_id = undefined
  form.site_id = undefined
  form.timeRange = undefined
  form.curtailed = false
  selectedCustomer.value = undefined
  selectedSite.value = undefined

  formRef.value?.clearValidate()
}

const onDialogOpened = () => {
  resetForm()

  // Initialize with props if provided
  if (props.selectedCustomer) {
    selectedCustomer.value = props.selectedCustomer
    form.customer_id = props.selectedCustomer.id
  }

  if (props.selectedSite) {
    selectedSite.value = props.selectedSite
    form.site_id = props.selectedSite.site_id
  }
}

// Reset form when dialog closes
const onDialogClosed = () => {
  resetForm()
}

const now = useNow()
const nowSiteTZFormatted = computed(() =>
  DateTime.fromJSDate(now.value, { zone: selectedSite.value?.timezone }).toFormat(
    "z, Z 'hours' (ZZZZ), MMMM d, yyyy, TT"
  )
)
</script>

<template>
  <el-dialog
    v-model="visible"
    title="Add Curtailment Interval"
    width="550px"
    align-center
    @open="onDialogOpened"
    @closed="onDialogClosed"
  >
    <el-form ref="formRef" :model="form" :rules="rules" label-position="top">
      <div class="flex flex-col gap-[var(--spacing-sm)] mb-[var(--spacing-sm)]">
        <!-- Customer -->
        <el-form-item label="Customer" prop="customer_id">
          <RcCustomerSelect v-model="selectedCustomer" placeholder="Select Customer" />
        </el-form-item>

        <!-- Site -->
        <el-form-item label="Site" prop="site_id">
          <RcSiteSelect
            v-model="selectedSite"
            :disabled="!selectedCustomer"
            :customer-id="selectedCustomer?.id"
            placeholder="Select Site"
          />
        </el-form-item>

        <!-- Timezone info -->
        <div v-if="selectedSite" class="bg-black/5 rounded py-2 px-3">
          <div v-if="!selectedSite.timezone" class="flex items-start gap-[var(--spacing-spacing-xs)]">
            <el-text type="warning" class="!m-0 text-[var(--fill-error)]"
              >In order to proceed, the site needs to have timezone set</el-text
            >
          </div>
          <div v-else class="flex flex-col gap-1">
            <div class="flex items-center gap-[var(--spacing-spacing-xs)]">
              <el-text type="info">Timezone</el-text>
              <p class="m-0 text-[var(--text-secondary)]">{{ nowSiteTZFormatted }}</p>
            </div>
          </div>
        </div>
      </div>

      <el-divider />

      <div class="flex flex-col gap-[var(--spacing-sm)] mb-[var(--spacing-sm)]">
        <el-form-item label="Time Range" prop="timeRange">
          <el-date-picker
            v-model="form.timeRange"
            type="datetimerange"
            range-separator="to"
            start-placeholder="Start date & time"
            end-placeholder="End date & time"
            format="YYYY-MM-DD HH:00"
            value-format="YYYY-MM-DDTHH:00:00"
            popper-class="cpm-add-hours__time-picker-popper"
            :disabled="!siteHasTimezone"
            style="width: 100%"
            :show-confirm="false"
          />
        </el-form-item>

        <p v-if="selectedHoursCount !== null" class="m-0 text-[13px] font-500 text-[var(--el-color-primary)]">
          {{ selectedHoursCount }} hour{{ selectedHoursCount === 1 ? '' : 's' }} selected
        </p>

        <p
          class="flex items-center gap-[var(--spacing-spacing-xs)] text-xs text-[var(--text-secondary)] mt-[var(--spacing-spacing-sm)] mb-0 leading-normal"
        >
          <Icon icon="material-symbols:warning-outline" :height="20" color="var(--text-badge-manz)" />
          Time range should be specified in the site's local time zone.
        </p>

        <el-form-item>
          <div class="flex items-center gap-2 pt-4">
            <el-checkbox v-model="form.curtailed" :disabled="!siteHasTimezone" />
            <span class="font-500">Curtailed</span>
          </div>
        </el-form-item>
      </div>
    </el-form>

    <template #footer>
      <div class="flex justify-end gap-3">
        <el-button size="large" @click="visible = false">Cancel</el-button>
        <el-button
          size="large"
          type="primary"
          :disabled="isSubmitting || !siteHasTimezone"
          :loading="isSubmitting"
          @click="submitForm"
        >
          Add
        </el-button>
      </div>
    </template>
  </el-dialog>
</template>

<style lang="scss" scoped>
:deep() {
  &.el-form-item__label {
    font-weight: 500;
  }

  &.el-dialog__body {
    padding-top: var(--spacing-spacing-md);
  }

  &.el-divider--horizontal {
    margin: var(--spacing-spacing-md) 0;
  }

  &.el-checkbox {
    margin-right: var(--spacing-spacing-none);
  }
}
</style>

<style lang="scss">
.cpm-add-hours__time-picker-popper {
  .el-time-spinner__wrapper:nth-child(n + 2) {
    display: none;
  }
}
</style>
